<template>
	<div class="buy-car-page">
		<!-- 购物车内容 -->
		<div v-if="showCar" class="contents">
			<BuyGoodsList
				ref="buyGoodsListRef"
				:lists="lists"
				:recommend-list="recommendList"
				@query-list="queryTotalBuyerCartList"
				@get-cart-purchase-zero="getCartPurchaseZero"
				@change-web-site="changeWebSite"
				@change-goods="changeGoods"
			/>

			<!-- 底部全选栏 -->
			<BotBar ref="botBarRef" :lists="lists" :is-all-checked="allChecked" @select-all="handleSelectAll" />

			<!-- 底部结算栏 -->
			<FooterBar :checks="3" />
		</div>

		<!-- 空状态 -->
		<div v-else class="empty-state">
			<NoData />
			<FooterBar :checks="3" />
		</div>
	</div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue';
import { showToast, closeToast } from 'vant';
import BuyGoodsList from './components/BuyGoodsList.vue';
import BotBar from './components/BotBar.vue';
import NoData from './components/NoData.vue';
import FooterBar from '@/components/FooterBar.vue';
import { queryTotalBuyerCartList as queryTotalBuyerCartListApi, getShoppingCartActivity, getCartPurchaseZero, editCartList } from '@/api/buyCar.js';
import { getMutil } from '@/api/index.js';
import { recommendProducts } from '@/api/index.js';
import { useCartStore } from '@/stores/cart';
import { useAnalytics } from '@/composables/useAnalytics';
import { useRoute } from 'vue-router';
import { getStorageSync, removeStorageSync, setStorageSync } from '@/utils/storage.js';
useSeoMeta({
	title: '国械在线-购物车',
	ogTitle: '国械在线-购物车',
	ogImage: 'https://uat-cmdc.oss-cn-beijing.aliyuncs.com/productFile/2022/08/18/9af2756f-27bf-4d1e-b0d1-8397e82bb2a0.png',
});

const route = useRoute();
const cartStore = useCartStore();
const { trackEvent, trackEventExposure, disconnectAllExposure } = useAnalytics();

const buyGoodsListRef = ref(null);
const botBarRef = ref(null);
const showCar = ref(true);
const lists = ref([]);
const buyCarIds = ref([]);
const recommendList = ref([]);
const allChecked = ref(false);
const guessLikeExposureRegistered = ref(false);

const ensureLoginOrRedirect = () => {
	const token = getStorageSync('cmdc_access_token_mobile');
	if (token) return true;

	// 记录当前页，登录成功后可按 stepRouter 回跳
	setStorageSync('stepRouter', route.fullPath || '/buyCar');
	navigateTo('/login', { replace: true });
	return false;
};

const initRepurchaseSelection = () => {
	const buyAgainFlag = getStorageSync('buyAgain');
	const cachedBuyCarIds = getStorageSync('buyCarIds');

	if (buyAgainFlag) {
		buyCarIds.value = Array.isArray(cachedBuyCarIds) ? cachedBuyCarIds : [];
		// 一次性消费“再次购买”标记，避免后续进入购物车仍被旧数据影响
		removeStorageSync('buyAgain');
		removeStorageSync('buyCarIds');
		return true;
	}

	buyCarIds.value = [];
	removeStorageSync('buyAgain');
	removeStorageSync('buyCarIds');
	return false;
};

// 获取猜你喜欢列表
const getRecommendProducts = async () => {
	try {
		const currentProductList =
			lists.value
				?.flatMap(
					sub =>
						sub?.list?.map(item => ({
							companyCode: sub.companyCode,
							productCode: item?.productCode,
							productId: item?.productId,
						})) || []
				)
				.flat()
				.filter(item => {
					return item && typeof item.productCode === 'string' && item.productCode.trim() !== '';
				}) || [];

		const params = {
			type: 4,
			companyCode: '',
			currentProductList,
		};

		const { code, data } = await recommendProducts(params);
		if (code == 200) {
			recommendList.value = data || [];
			// 加购成功会再次刷新推荐列表，曝光监听只注册一次
			if (!guessLikeExposureRegistered.value && recommendList.value.length > 0) {
				await nextTick();
				trackEventExposure('.guess-like', {
					module: '购物车',
					eventCode: 'cart_page__recommended_module_exposure',
					eventName: '购物车猜你喜欢曝光',
					attrCode: 'module_name',
					attrName: '猜你喜欢',
				});
				guessLikeExposureRegistered.value = true;
			}
		}
	} catch (e) {
		console.error('获取推荐商品失败:', e);
		recommendList.value = [];
	}
};

// 查询购物车列表
const queryTotalBuyerCartList = async (buyAgain = false) => {
	showToast({
		message: '加载中...',
		type: 'loading',
		duration: 0,
		mask: true,
		forbidClick: true,
		loadingType: 'spinner', // 关键！设置为 spinner 就是放射状图标
	});

	try {
		const mu = await getMutil();
		setStorageSync('getMutil', mu.data);

		const queryCartParams = {
			pageStart: 1,
			pageSize: 50,
			pageNumber: 1,
			productName: '',
			materialCode: '',
			manufacturer: '',
			licenseCode: '',
			filialeCode: '',
			timeSortStatus: 1,
		};

		if (buyAgain) {
			delete queryCartParams.timeSortStatus;
		}

		const token = getStorageSync('cmdc_access_token_mobile');
		if (!token) return;

		const res = await queryTotalBuyerCartListApi(queryCartParams);

		if (res.code == 200) {
			const cartLists = res.data.list || [];
			showCar.value = !!cartLists.length;

			for (let i = 0; i < cartLists.length; i++) {
				let item = cartLists[i];
				item.checked = item.list.every(list => buyCarIds.value.includes(list.buyerCartId) || buyCarIds.value.includes(list.productId));
				item.couponActivityVo = [];

				item.list.forEach(list => {
					list.purchaseZeroProductList = [];
					list.checked = buyCarIds.value.includes(list.buyerCartId) || buyCarIds.value.includes(list.productId);

					if (list.checked && list.ippStatus === 101) {
						if (list.ippMiniPurchaseNum) {
							if (list.quantity < list.ippMiniPurchaseNum) {
								list.quantity = list.ippMiniPurchaseNum;
							} else {
								const remainder = (list.quantity - list.ippMiniPurchaseNum) % list.ippPurchaseMultiple;
								if (remainder > 0) {
									const beis = Math.floor((list.quantity - list.ippMiniPurchaseNum) / list.ippPurchaseMultiple);
									list.quantity = list.ippMiniPurchaseNum + list.ippPurchaseMultiple * beis;
								}
							}
						}

						if (list.ippMiniPurchaseNum > 1) {
							editCartList({
								buyerCartId: list.buyerCartId,
								modifyQuantity: list.quantity,
								subtotal: list.quantity * list.price,
							});
						}
					}
				});

				getShoppingCartActivityData(item);
			}

			await getCartPurchaseZeroData(cartLists);
			lists.value = cartLists;
			changeAll(cartLists.every(item => item.checked));

			nextTick(() => {
				buyGoodsListRef.value?.resetTop();
			});

			cartStore.setCartCount(res.data?.total || 0);
			await getRecommendProducts();
		} else {
			showToast({
				message: res.message,
				icon: 'none',
				duration: 3000,
			});
		}
	} catch (error) {
		console.error('查询购物车失败:', error);
		showToast({
			message: '加载失败，请重试',
			icon: 'none',
		});
	} finally {
		closeToast();
	}
};

// 获取购物车活动
const getShoppingCartActivityData = async item => {
	try {
		const res = await getShoppingCartActivity({
			companyCode: item.companyCode,
			orderProductList: item.list.map(product => ({
				productId: product.productId,
				productCode: product.productCode,
				productPrice: product.price,
				purchaseQuantity: product.quantity,
				agreementPriceId: product.agreementPriceId,
				areaName: product.areaName,
				imageUrl: product.imageUrl,
				checked: false,
			})),
		});
		item.couponActivityVo = res.data.couponActivityVo || [];
	} catch (err) {
		console.error('获取购物车活动失败:', err);
		item.couponActivityVo = [];
	}
};

// 获取买赠商品
const getCartPurchaseZeroData = async cartLists => {
	try {
		const flatList = cartLists.map(item => item.list).flat();
		const params = flatList.map((product, index) => ({
			buyerCartId: index,
			purchaseQuantity: product.quantity,
			productCode: product.productCode,
			companyCode: product.companyCode,
		}));

		const res = await getCartPurchaseZero(params);
		const purchaseZeroProductList = res.data;

		cartLists.forEach(item1 => {
			item1.list.forEach(item2 => {
				purchaseZeroProductList.forEach(purchase => {
					if (item1.companyCode === purchase.companyCode && item2.productCode === purchase.productCode) {
						item2.purchaseZeroProductList = purchase.purchaseZeroProductList;
					}
				});
			});
		});

		console.log('%c [ cartLists ]-210', 'font-size:13px; background:#3472f2; color:#78b6ff;', cartLists);
	} catch (err) {
		console.error('获取买赠商品失败:', err);
	}
};

// 全选处理
const handleSelectAll = val => {
	buyGoodsListRef.value?.chooseAll(val);
};

// 改变全选状态
const changeAll = val => {
	// 通过 ref 调用 BotBar 的 changeAll 方法
	botBarRef.value?.changeAll(val);
};

// 更新公司的选中状态（当商品变化时）
const updateCompanyCheckedStatus = companyId => {
	lists.value.forEach(item => {
		if (item.companyId == companyId) {
			// 如果所有有效商品都选中，则公司复选框选中
			item.checked = item.list.every(goods => (goods.invalidSign !== 1 && goods.checked == true) || goods.invalidSign == 1);
		}
	});
};

// 更新全选状态（当公司或商品变化时）
const updateAllCheckedStatus = () => {
	// 如果所有公司都选中，则全选按钮选中
	if (lists.value.every(item => item.checked == true)) {
		allChecked.value = true;
		changeAll(true);
	}

	// 如果有任何公司未选中，则全选按钮取消
	if (lists.value.some(item => item.checked == false)) {
		allChecked.value = false;
		changeAll(false);
	}
};

// 子站复选框发生改变
const changeWebSite = webSite => {
	lists.value.forEach(_webSite => {
		if (_webSite.companyId == webSite.companyId) {
			_webSite.list.forEach(goods => {
				if (goods.invalidSign !== 1) {
					goods.checked = webSite.checked;
				}
			});
		}
	});

	// 更新全选状态
	updateAllCheckedStatus();
};

// 子站商品复选框发生改变
const changeGoods = (webSite, goods) => {
	lists.value.forEach(_webSite => {
		if (_webSite.companyId == webSite.companyId) {
			_webSite.list.forEach(_goods => {
				if (_goods.buyerCartId == goods.buyerCartId && _goods.invalidSign !== 1) {
					_goods.checked = goods.checked;
				}
			});
		}
	});

	// 更新公司和全选状态
	updateCompanyCheckedStatus(webSite.companyId);
	updateAllCheckedStatus();
};

onMounted(() => {
	if (!ensureLoginOrRedirect()) return;
	trackEvent({
		module: '购物车',
		eventCode: 'cart_page_enter',
		eventName: '进入购物车',
	});
	const buyAgain = initRepurchaseSelection();
	// 初始化购物车数据
	queryTotalBuyerCartList(buyAgain);
});

onUnmounted(() => {
	disconnectAllExposure();
});
</script>

<style scoped lang="scss">
.buy-car-page {
	min-height: 100vh;
	background: #f5f5f5;
}

.contents {
	padding-bottom: 0; // 不需要 padding，BuyGoodsList 自己处理滚动
}

.empty-state {
	// min-height: calc(100vh - 200px);
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
}

// /deep/ .tips-del {
// 	display: block;
// 	margin: 0.5rem 0 0 0;
// 	padding: 0.3rem 1rem;
// 	background: #ffe7c3;
// 	color: #eb3535;
// 	font-size: 1rem;

// 	.info {
// 		font-weight: 900;
// 		position: relative;
// 		top: 0.1rem;
// 		margin-right: 0.2rem;
// 	}
// }
</style>
